import { useEffect, useRef, useState } from 'react'
import { Platform } from 'react-native'

import {
  markNativeModalTransition,
  msUntilNativeModalTransitionsSettle,
} from '../helpers/nativeModalDismiss.helper'

const isIos = () => Platform.OS === 'ios'

/**
 * Visibility to hand to a native `<Modal>` so it is never presented mid-transition.
 *
 * iOS refuses to present a Modal while another one is still presenting or animating away, and RN
 * then treats the refused Modal as shown: it never appears and an invisible layer swallows every
 * touch (specs/coin-compliance Story 5, specs/profile-page Story 8). On iOS a request to show waits
 * until the last shared-modal transition has settled, then claims the slot; hiding and unmounting
 * while shown also count as transitions. Android and web present modals independently: the
 * requested visibility is returned unchanged.
 */
export function useSettledModalVisibility(isVisible: boolean): boolean {
  const [shown, setShown] = useState(false)
  const shownRef = useRef(shown)
  shownRef.current = shown

  useEffect(() => {
    if (!isIos()) return undefined
    if (!isVisible) {
      if (shown) {
        markNativeModalTransition()
        setShown(false)
      }
      return undefined
    }
    if (shown) return undefined

    let timer: ReturnType<typeof setTimeout> | null = null
    const showWhenSettled = () => {
      const wait = msUntilNativeModalTransitionsSettle()
      if (wait > 0) {
        timer = setTimeout(showWhenSettled, wait)
        return
      }
      timer = null
      // Claim the slot now, so a modal asked to show in the same frame waits for this one.
      markNativeModalTransition()
      setShown(true)
    }
    showWhenSettled()
    return () => {
      if (timer) clearTimeout(timer)
    }
  }, [isVisible, shown])

  // Unmounting while shown dismisses the native Modal too.
  useEffect(
    () => () => {
      if (isIos() && shownRef.current) markNativeModalTransition()
    },
    []
  )

  return isIos() ? shown : isVisible
}
